import React, { useEffect, useMemo } from 'react';
import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CardContent,
  FilePdfIcon,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay } from '@/components';
import { useAppContext } from '@/context';
import { useFRCallback } from '@/hooks';
import { TwoFAIcon01 } from '@/static';

const faqLink =
  'https://essentials.availity.com/availity/help-open/source/portal_providers/account_administration/2-step/_topics/c_faqs_for_2step_authentication.html';

const backupCodesInputVerbiage = 'Backup codes must be 10 characters and contain no spaces.';

function TwoFAInputBackupCodeV2({ ...props }) {
  const { stepToRender, setCurrentStep, errorCode } = props;
  const { callbacks = [], confirmationCallbackIndex, confirmationCallbackOptions } = useFRCallback(stepToRender);

  const { setMessages } = useAppContext();

  // build form handler
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      code: '',
    },
  });

  // set field error if an errorCode comes back
  useEffect(() => {
    if (errorCode === '106') {
      setError('code', { type: 'custom', message: 'Enter a valid backup code.' });
    }
  }, [errorCode]);

  const hasBackNavigation = useMemo(() => {
    let output = false;

    if (callbacks) {
      for (const option of confirmationCallbackOptions) {
        const formattedOption = option?.toLowerCase();
        if (formattedOption === 'back') {
          output = true;
        }
      }
    }

    return output;
  }, [callbacks]);

  const onSubmit = (values) => {
    const { code = '' } = values;
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // pre-format code - set to all uppercase and trim all spaces
    const uppercaseCode = code?.toUpperCase();
    const formattedCode = uppercaseCode.replaceAll(/\s/g, '');

    // get index of name callback
    const nameCallbackObjIndex = findIndex(
      callbacks,
      (callbackObj) => callbackObj?.type?.toLowerCase() === 'namecallback'
    );

    // confirm index of the 'verify' button in the navigation options
    const navigationOptionsIndex = findIndex(
      callbacks[confirmationCallbackIndex]?.output,
      (outputObj) => outputObj?.name?.toLowerCase() === 'options'
    );
    const verifyButtonIndex = findIndex(
      callbacks[confirmationCallbackIndex]?.output[navigationOptionsIndex].value,
      (option) => option?.toLowerCase() === 'verify'
    );

    // set the name callback input
    newCallbacks[nameCallbackObjIndex].input[0] = {
      ...newCallbacks[nameCallbackObjIndex].input[0],
      value: formattedCode,
    };

    // set the confirmation callback input
    newCallbacks[confirmationCallbackIndex].input[0] = {
      ...newCallbacks[confirmationCallbackIndex].input[0],
      value: verifyButtonIndex,
    };

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep(nextStep);
  };

  const handleNavigation = (selected) => {
    const nextStep = { ...stepToRender };

    if (selected === 'back') {
      const newCallbacks = [...callbacks];

      // get index of back navigation
      const backNavigationIndex = findIndex(confirmationCallbackOptions, (option) => option?.toLowerCase() === 'back');

      // set the confirmation callback input based off the index of the selected option in the callback
      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: backNavigationIndex,
      };

      // set the newCallbacks in the newStep
      Object.assign(nextStep.payload.callbacks, newCallbacks);

      // update the currentStep to trigger navigation
      setCurrentStep(nextStep);
    }
  };

  return (
    <Grid
      id="2fa-input-backup-code-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-start-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Grid display="flex" flexDirection="row" flexWrap="wrap">
                  <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                    <Box textAlign="center" mb={2}>
                      <img
                        src={TwoFAIcon01}
                        alt="2FA Icon"
                        style={{
                          maxWidth: '45%',
                        }}
                      />
                    </Box>
                    <Typography variant="h5" component="h3" mb={2}>
                      Don&apos;t lose access, update now!
                    </Typography>
                    <Alert severity="warning" icon={false}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                        Text and Voice authentication are going away soon.
                      </Typography>
                      <Typography variant="subtitle1" mb={2}>
                        Make sure to set up a new authentication method using an authenticator app (like Google
                        Authenticator) to continue accessing Essentials.
                      </Typography>
                      <Typography variant="subtitle1">Instructions are available in the FAQ link below.</Typography>
                    </Alert>
                    <Box mt={3}>
                      <Link
                        href={faqLink}
                        target="_blank"
                        loadApp={false}
                        rel="noopener noreferrer"
                        aria-label="Get answers to common questions (opens PDF in a new window)"
                      >
                        2-Step Authentication FAQs <FilePdfIcon fontSize="small" />
                      </Link>
                    </Box>
                  </Grid>
                  <Grid
                    display="flex"
                    flexDirection="column"
                    size={{ md: 8 }}
                    sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                  >
                    <Box pl={2}>
                      <Typography variant="h1" mb={1}>
                        2-Step Authentication
                      </Typography>
                      <Typography variant="h4" component="h2" mb={2}>
                        Protect Your Account
                      </Typography>
                    </Box>
                    <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                      <Typography variant="subtitle1" mt={2} mb={3}>
                        In order to recover your account, you will need to enter in one of the backup codes that you
                        received upon securing your account for the first time.
                      </Typography>
                      <Typography variant="subtitle1" mb={3}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          Please Note:
                        </Box>{' '}
                        Each backup code may only be used once. Using a backup code will require you to provide 2-step
                        authentication next time you login.
                      </Typography>
                      <Box>
                        <TextField
                          id="code"
                          label="Backup Code"
                          margin="normal"
                          fullWidth={false}
                          autoFocus
                          onFocus={() => setMessages([])}
                          {...register('code', {
                            pattern: {
                              value: /^[\dA-Za-z]*$/,
                              message: backupCodesInputVerbiage,
                            },
                            maxLength: {
                              value: 10,
                              message: backupCodesInputVerbiage,
                            },
                            minLength: {
                              value: 10,
                              message: backupCodesInputVerbiage,
                            },
                            required: backupCodesInputVerbiage,
                          })}
                          error={!!errors.code}
                          helperText={errors.code?.message || backupCodesInputVerbiage}
                        />
                      </Box>
                      <MessageDisplay />
                      <Box textAlign="right" mt="auto">
                        {hasBackNavigation && (
                          <Button color="secondary" onClick={() => handleNavigation('back')}>
                            Back
                          </Button>
                        )}
                        <Button color="primary" sx={{ marginLeft: 1 }} type="submit">
                          Continue
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </form>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
}

TwoFAInputBackupCodeV2.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
};

export default TwoFAInputBackupCodeV2;
